import { useState, useEffect } from "react";
import { openExternal } from "../lib/open-external";
import { Heart, ExternalLink, Check } from "lucide-react";
import { T, withAlpha } from "../tokens";
import { Badge } from "./Badge";
import { ScoreRing } from "./ScoreRing";
import type { MatchWithJob } from "@jobreel/shared";

interface Props {
  match:    MatchWithJob;
  featured?: boolean;
  onOpen?:  () => void;
  onSave?:  (matchId: string) => void;
  onTrack?: (matchId: string) => void;
}

// Mirrors the breakpoint pattern used in AppShell / MatchDetailDrawer. On touch
// there is no hover, so action icons are shown always and the meta line wraps.
function useIsMobile(bp = 720): boolean {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < bp);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return mobile;
}

type TagVariant = "ok" | "info" | "teal" | "default";

// The fit/family tag is only partially backed by the data: semantic-only rows
// carry `familyFit.adjacencyType`; full-LLM rows omit it (they carry sub-scores).
// So: show the family-adjacency tag when present, otherwise fall back to the
// scoring-tier label derived from `modelUsed` ("deep" vs "quick" match).
function fitTag(match: MatchWithJob): { label: string; variant: TagVariant } | null {
  const adj = match.scoreBreakdown?.familyFit?.adjacencyType;
  switch (adj) {
    case "same":
    case "matched_family": return { label: "strong fit",   variant: "ok" };
    case "adjacent":       return { label: "adjacent",     variant: "info" };
    case "non_adjacent":   return { label: "cross-family", variant: "default" };
    case "uncertain":      return { label: "unclear fit",  variant: "default" };
    case "no_penalty":     return { label: "fit",          variant: "default" };
  }
  const m = match.modelUsed ?? "";
  if (m.startsWith("llm_scored")) return { label: "deep match",  variant: "teal" };
  if (m)                          return { label: "quick match", variant: "default" };
  return null;
}

function IconBtn({ title, hovered, onClick, children }: {
  title: string; hovered: boolean; onClick: (e: React.MouseEvent) => void; children: React.ReactNode;
}) {
  const [ih, setIh] = useState(false);
  return (
    <button
      type="button" title={title} aria-label={title}
      onMouseEnter={() => setIh(true)} onMouseLeave={() => setIh(false)}
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 30, height: 30, borderRadius: 7, flexShrink: 0,
        background: ih ? T.s3 : "transparent",
        border: `1px solid ${ih ? T.b2 : "transparent"}`,
        color: ih ? T.t0 : (hovered ? T.t1 : T.t2),
        cursor: "pointer", transition: "all 0.12s ease", padding: 0,
      }}
    >
      {children}
    </button>
  );
}

export function JobCard({ match, featured, onOpen, onSave, onTrack }: Props) {
  const [h, setH] = useState(false);
  const isMobile  = useIsMobile();
  const score     = Math.round(match.weightedScore);
  const daysAgo   = match.postedAt
    ? Math.floor((Date.now() - new Date(match.postedAt).getTime()) / 86_400_000)
    : null;
  const fresh   = daysAgo !== null && daysAgo <= 3;
  const remote  = match.workAuthTag?.includes("remote") ?? false;
  const tag     = fitTag(match);
  const actionsVisible = isMobile || h;

  function save(e: React.MouseEvent)  { e.stopPropagation(); onSave?.(match.matchId); }
  function track(e: React.MouseEvent) { e.stopPropagation(); onTrack?.(match.matchId); }
  function openPortal(e: React.MouseEvent) {
    e.stopPropagation();
    openExternal(match.url);
  }

  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={onOpen}
      style={{
        display: "flex", alignItems: "center", gap: "12px",
        background: h ? T.s2 : T.s1,
        border: `1px solid ${featured ? T.tealBdr : h ? T.b2 : T.b0}`,
        borderRadius: "10px", padding: "10px 12px",
        transition: "background 0.15s ease, border-color 0.15s ease", cursor: "pointer",
        position: "relative", overflow: "hidden",
      }}
    >
      {featured && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px",
          background: `linear-gradient(90deg, transparent, ${withAlpha(T.teal, 0.5)}, transparent)` }}/>
      )}

      <ScoreRing score={score} size={40}/>

      {/* Middle — title + meta, both single-column, ellipsized */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{
          fontFamily: T.display, fontSize: "15px", color: T.t0, margin: 0, fontWeight: 600, lineHeight: 1.3,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {match.title}
        </h3>
        <div style={{
          display: "flex", alignItems: "center", gap: "6px", marginTop: "3px",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}>
          <span style={{
            fontFamily: T.body, fontSize: "11.5px", color: T.t2, minWidth: 0, flexShrink: 1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {match.company} · {match.location ?? "Location N/A"}{match.salaryRaw ? ` · ${match.salaryRaw}` : ""}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
            {featured && <Badge variant="teal" size="sm">best</Badge>}
            {daysAgo !== null && (
              <Badge variant={fresh ? "ok" : "default"} size="sm">
                {fresh ? "● " : ""}{daysAgo === 0 ? "today" : `${daysAgo}d`}
              </Badge>
            )}
            {remote && <Badge variant="info" size="sm">remote</Badge>}
            {tag && <Badge variant={tag.variant} size="sm">{tag.label}</Badge>}
          </span>
        </div>
      </div>

      {/* Actions — hover-revealed on desktop, always shown on mobile */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: "flex", alignItems: "center", gap: "2px", flexShrink: 0,
          opacity: actionsVisible ? 1 : 0,
          transition: "opacity 0.12s ease",
          pointerEvents: actionsVisible ? "auto" : "none",
        }}
      >
        <IconBtn title="Save" hovered={h} onClick={save}>
          <Heart size={15} strokeWidth={1.8}/>
        </IconBtn>
        {match.url
          ? <IconBtn title="Open portal" hovered={h} onClick={openPortal}>
              <ExternalLink size={15} strokeWidth={1.8}/>
            </IconBtn>
          : <IconBtn title="Mark applied" hovered={h} onClick={track}>
              <Check size={15} strokeWidth={1.8}/>
            </IconBtn>
        }
      </div>
    </div>
  );
}
